/**
 * Fit Intelligence Widget Bundle - Enhanced UI version
 */
var FitIntelligenceWidgetBundle = (() => {
  "use strict";

  // Default theme settings
  const DEFAULT_THEME = {
    primaryColor: "#2563eb",
    secondaryColor: "#64748b",
    textColor: "#1e293b",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    fontFamily: "Outfit, Inter, system-ui, -apple-system, sans-serif"
  };

  // 1. Transport Layer (Fetch with retry)
  const defaultRetryConfig = {
    maxRetries: 3,
    initialDelay: 1000,
    maxDelay: 10000,
    factor: 2,
    jitter: true
  };

  class Transport {
    constructor(config = defaultRetryConfig) {
      this.config = config;
    }
    async request(url, options) {
      let lastError = null;
      let delay = this.config.initialDelay;
      for (let i = 0; i <= this.config.maxRetries; i++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          const response = await fetch(url, { ...options, signal: controller.signal });
          clearTimeout(timeoutId);
          if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status}`);
          }
          return await response.json();
        } catch (err) {
          lastError = err;
          if (i === this.config.maxRetries) break;
          const backoff = this.config.jitter ? delay * (0.5 + Math.random()) : delay;
          await new Promise(resolve => setTimeout(resolve, backoff));
          delay = Math.min(delay * this.config.factor, this.config.maxDelay);
        }
      }
      throw lastError || new Error("Request failed");
    }
  }

  // 2. Analytics Batcher
  class AnalyticsBatcher {
    constructor(baseUrl) {
      this.baseUrl = baseUrl;
      this.queue = [];
      this.flushInterval = 5000;
      this.timer = null;
      this.STORAGE_KEY = "fit_analytics_queue";
      this.loadFromStorage();
      this.startTimer();
      this.attachUnloadListener();
    }
    track(type, payload) {
      const event = { type, payload, timestamp: Date.now() };
      this.queue.push(event);
      this.saveToStorage();
      if (this.queue.length >= 10) {
        this.flush();
      }
    }
    async flush() {
      if (this.queue.length === 0 || !navigator.onLine) return;
      const batch = [...this.queue];
      this.queue = [];
      this.saveToStorage();
      try {
        await fetch(`${this.baseUrl}/analytics/batch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ events: batch }),
          keepalive: true
        });
      } catch (err) {
        // Restore items to queue if failed
        this.queue = [...batch, ...this.queue];
        this.saveToStorage();
      }
    }
    startTimer() {
      this.timer = setInterval(() => this.flush(), this.flushInterval);
    }
    loadFromStorage() {
      try {
        const val = localStorage.getItem(this.STORAGE_KEY);
        if (val) this.queue = JSON.parse(val);
      } catch (err) {
        this.queue = [];
      }
    }
    saveToStorage() {
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.queue));
      } catch (err) {}
    }
    attachUnloadListener() {
      window.addEventListener("beforeunload", () => this.flush());
    }
  }

  // 3. Theme Engine
  class ThemeEngine {
    apply(theme = {}, container) {
      const active = { ...DEFAULT_THEME, ...theme };
      container.style.setProperty("--fit-primary", active.primaryColor);
      container.style.setProperty("--fit-secondary", active.secondaryColor);
      container.style.setProperty("--fit-text", active.textColor);
      container.style.setProperty("--fit-bg", active.backgroundColor);
      container.style.setProperty("--fit-radius", active.borderRadius);
      container.style.setProperty("--fit-font", active.fontFamily);
    }
  }

  // 4. Accessibility Manager
  class AccessibilityManager {
    constructor() {
      this.lastActiveElement = null;
    }
    trapFocus(container) {
      this.lastActiveElement = document.activeElement;
      const focusables = container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusables.length > 0) {
        focusables[0].focus();
      }
      container.addEventListener("keydown", (e) => this.handleTabKey(e, container));
    }
    releaseFocus() {
      if (this.lastActiveElement) {
        this.lastActiveElement.focus();
      }
    }
    handleTabKey(e, container) {
      if (e.key !== "Tab") return;
      const focusables = Array.from(container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
    announce(text) {
      let announcer = document.getElementById("fit-a11y-announcer");
      if (!announcer) {
        announcer = document.createElement("div");
        announcer.id = "fit-a11y-announcer";
        announcer.setAttribute("aria-live", "polite");
        announcer.style.position = "absolute";
        announcer.style.width = "1px";
        announcer.style.height = "1px";
        announcer.style.overflow = "hidden";
        document.body.appendChild(announcer);
      }
      announcer.textContent = text;
    }
  }

  // 5. Fit Widget
  class FitWidget {
    constructor(config) {
      this.config = {
        baseUrl: "http://localhost:3000",
        theme: {},
        hooks: {},
        locale: "en",
        ...config
      };
      this.isOpen = false;
      this.transport = new Transport();
      this.analytics = new AnalyticsBatcher(this.config.baseUrl);
      this.themeEngine = new ThemeEngine();
      this.a11y = new AccessibilityManager();
      this.init();
    }
    init() {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.mount());
      } else {
        this.mount();
      }
    }
    mount() {
      this.container = document.createElement("div");
      this.container.id = "fit-intelligence-widget";
      this.container.className = "fit-widget-container";
      this.themeEngine.apply(this.config.theme, this.container);
      document.body.appendChild(this.container);
      this.renderTrigger();
      if (this.config.hooks && this.config.hooks.onLoaded) {
        this.config.hooks.onLoaded();
      }
      this.analytics.track("widget_loaded", { brandId: this.config.brandId });
    }
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.renderModal();
      this.a11y.trapFocus(this.container);
      if (this.config.hooks && this.config.hooks.onOpen) {
        this.config.hooks.onOpen();
      }
      this.analytics.track("widget_opened", {
        productId: this.config.productId,
        externalProductId: this.config.externalProductId
      });
    }
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.renderTrigger();
      this.a11y.releaseFocus();
      if (this.config.hooks && this.config.hooks.onClose) {
        this.config.hooks.onClose();
      }
      this.analytics.track("widget_closed", {});
    }
    renderTrigger() {
      if (!this.container) return;
      this.container.innerHTML = `
        <button class="fit-trigger-btn" aria-label="Find my size">
          <svg class="fit-ruler-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21.3 15.3a2.82 2.82 0 0 1 0 4c-1 1-2.5 1-3.5 0L2.8 4.3a2.82 2.82 0 0 1 0-4c1-1 2.5-1 3.5 0Z"/>
            <path d="m5.6 7.2 1.4-1.4"/>
            <path d="m7.2 10.4 1.4-1.4"/>
            <path d="m10.4 12 1.4-1.4"/>
            <path d="m12 15.2 1.4-1.4"/>
            <path d="m15.2 16.8 1.4-1.4"/>
          </svg>
          Find My Size
        </button>
      `;
      const btn = this.container.querySelector("button");
      if (btn) {
        btn.addEventListener("click", () => this.open());
      }
    }
    renderModal() {
      if (!this.container) return;
      this.container.innerHTML = `
        <div class="fit-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="fit-title">
          <div class="fit-modal-content">
            <button class="fit-close-btn" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div class="fit-modal-header">
              <h2 id="fit-title">Find Your Perfect Fit</h2>
              <p class="fit-subtitle">Enter your details for an instant size recommendation</p>
            </div>
            <div id="fit-widget-body">
              <form id="fit-sizing-form">
                <div class="fit-form-grid">
                  <div class="fit-form-group">
                    <label for="fit-height">Height</label>
                    <div class="fit-input-wrapper">
                      <input type="number" id="fit-height" name="height" min="100" max="250" required value="175" />
                      <span class="fit-unit">cm</span>
                    </div>
                  </div>
                  <div class="fit-form-group">
                    <label for="fit-weight">Weight</label>
                    <div class="fit-input-wrapper">
                      <input type="number" id="fit-weight" name="weight" min="30" max="200" required value="70" />
                      <span class="fit-unit">kg</span>
                    </div>
                  </div>
                </div>

                <div class="fit-form-grid">
                  <div class="fit-form-group">
                    <label for="fit-gender">Gender</label>
                    <div class="fit-select-wrapper">
                      <select id="fit-gender" name="gender" required>
                        <option value="female" selected>Female</option>
                        <option value="male">Male</option>
                        <option value="unisex">Unisex</option>
                      </select>
                    </div>
                  </div>
                  <div class="fit-form-group">
                    <label for="fit-preference">Fit Preference</label>
                    <div class="fit-select-wrapper">
                      <select id="fit-preference" name="fitPreference" required>
                        <option value="slim">Slim</option>
                        <option value="regular" selected>Regular</option>
                        <option value="relaxed">Relaxed</option>
                        <option value="oversized">Oversized</option>
                      </select>
                    </div>
                  </div>
                </div>

                <button type="submit" class="fit-submit-btn">
                  <span>Get Recommendation</span>
                  <svg class="fit-arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
              </form>

              <div id="fit-result" style="display: none;">
                <div class="fit-result-card">
                  <div class="fit-size-badge-outer">
                    <span class="fit-size-label">RECOMMENDED SIZE</span>
                    <div class="fit-size-badge">
                      <span id="fit-recommended-size" class="fit-size-val">-</span>
                    </div>
                  </div>

                  <div class="fit-confidence-section">
                    <div class="fit-confidence-header">
                      <span>Confidence Level</span>
                      <span class="fit-confidence-val"><span id="fit-confidence">0</span>%</span>
                    </div>
                    <div class="fit-progress-bg">
                      <div id="fit-progress-fill" class="fit-progress-fill" style="width: 0%"></div>
                    </div>
                  </div>

                  <div class="fit-reasons-section">
                    <h4>Why this size works:</h4>
                    <ul id="fit-reasons" class="fit-reasons-list"></ul>
                  </div>
                </div>
                
                <button id="fit-reset-btn" class="fit-secondary-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
                  </svg>
                  Recalculate Size
                </button>
              </div>

              <div id="fit-error" style="display: none;"></div>
            </div>
          </div>
        </div>
      `;

      const closeBtn = this.container.querySelector(".fit-close-btn");
      if (closeBtn) {
        closeBtn.addEventListener("click", () => this.close());
      }

      const resetBtn = this.container.querySelector("#fit-reset-btn");
      if (resetBtn) {
        resetBtn.addEventListener("click", () => {
          this.container.querySelector("#fit-sizing-form").style.display = "block";
          this.container.querySelector("#fit-result").style.display = "none";
          this.container.querySelector("#fit-error").style.display = "none";
        });
      }

      const form = this.container.querySelector("#fit-sizing-form");
      if (form) {
        form.addEventListener("submit", async (e) => {
          e.preventDefault();
          const targetForm = e.target;
          const height = parseInt(targetForm.elements.namedItem("height").value, 10);
          const weight = parseInt(targetForm.elements.namedItem("weight").value, 10);
          const fitPreference = targetForm.elements.namedItem("fitPreference").value;
          const gender = targetForm.elements.namedItem("gender").value;

          const submitBtn = targetForm.querySelector(".fit-submit-btn");
          const originalText = submitBtn.innerHTML;

          try {
            submitBtn.innerHTML = `
              <span class="fit-spinner"></span>
              <span>Calculating...</span>
            `;
            submitBtn.disabled = true;

            const res = await this.getRecommendation({
              height,
              weight,
              fitPreference,
              gender
            });

            targetForm.style.display = "none";
            const resultDiv = this.container.querySelector("#fit-result");
            resultDiv.style.display = "block";
            resultDiv.querySelector("#fit-recommended-size").textContent = res.recommendedSize;
            resultDiv.querySelector("#fit-confidence").textContent = String(res.confidence);
            
            const progressFill = resultDiv.querySelector("#fit-progress-fill");
            if (progressFill) {
              progressFill.style.setProperty("width", `${res.confidence}%`, "important");
            }

            const reasonsUl = resultDiv.querySelector("#fit-reasons");
            reasonsUl.innerHTML = "";
            res.reasons.forEach((reason) => {
              const li = document.createElement("li");
              li.innerHTML = `
                <svg class="fit-check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>${reason}</span>
              `;
              reasonsUl.appendChild(li);
            });
          } catch (err) {
            const errDiv = this.container.querySelector("#fit-error");
            errDiv.textContent = err.message || "Failed to get recommendation.";
            errDiv.style.display = "block";
          } finally {
            if (submitBtn) {
              submitBtn.innerHTML = originalText;
              submitBtn.disabled = false;
            }
          }
        });
      }
    }
    async getRecommendation(userInput) {
      try {
        const payload = {
          accountId: this.config.brandId,
          user: userInput
        };
        if (this.config.productId) payload.productId = this.config.productId;
        if (this.config.externalProductId) {
          payload.externalProductId = String(this.config.externalProductId);
        }

        const res = await this.transport.request(
          `${this.config.baseUrl}/api/v1/recommendation`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
          }
        );

        if (this.config.hooks && this.config.hooks.onRecommendation) {
          this.config.hooks.onRecommendation(res);
        }

        this.analytics.track("recommendation_viewed", {
          productId: this.config.productId,
          externalProductId: this.config.externalProductId,
          size: res.recommendedSize,
          confidence: res.confidence
        });

        return res;
      } catch (err) {
        if (this.config.hooks && this.config.hooks.onError) {
          this.config.hooks.onError(err);
        }
        this.a11y.announce("Failed to get recommendation. Please try again.");
        throw err;
      }
    }
  }

  // 6. Enhanced UI Styles CSS
  const stylesCSS = `
:root {
  --fit-primary: #2563eb;
  --fit-secondary: #64748b;
  --fit-text: #1e293b;
  --fit-bg: #ffffff;
  --fit-radius: 12px;
  --fit-font: Outfit, Inter, system-ui, -apple-system, sans-serif;
  --fit-success: #10b981;
}

#fit-intelligence-widget {
  font-family: var(--fit-font) !important;
  color: var(--fit-text) !important;
  box-sizing: border-box !important;
}

#fit-intelligence-widget * {
  box-sizing: border-box !important;
  margin: 0 !important;
  padding: 0 !important;
}

#fit-intelligence-widget .fit-trigger-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 10px !important;
  background-color: var(--fit-primary) !important;
  color: white !important;
  padding: 0px 28px !important;
  height: 48px !important;
  min-height: 48px !important;
  border-radius: var(--fit-radius) !important;
  border: none !important;
  cursor: pointer !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  font-family: var(--fit-font) !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08) !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  line-height: 48px !important;
}

#fit-intelligence-widget .fit-trigger-btn:hover {
  filter: brightness(0.95) !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
}

#fit-intelligence-widget .fit-trigger-btn:active {
  transform: translateY(0) !important;
}

#fit-intelligence-widget .fit-ruler-icon {
  width: 18px !important;
  height: 18px !important;
  display: inline-block !important;
  vertical-align: middle !important;
}

#fit-intelligence-widget .fit-modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background-color: rgba(15, 23, 42, 0.5) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 99999 !important;
  backdrop-filter: blur(8px) !important;
  padding: 16px !important;
  animation: fit-fade-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards !important;
}

#fit-intelligence-widget .fit-modal-content {
  background-color: var(--fit-bg) !important;
  padding: 40px 32px !important;
  border-radius: var(--fit-radius) !important;
  width: 100% !important;
  max-width: 480px !important;
  position: relative !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15) !important;
  animation: fit-slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards !important;
}

#fit-intelligence-widget .fit-close-btn {
  position: absolute !important;
  top: 20px !important;
  right: 20px !important;
  border: none !important;
  background: transparent !important;
  cursor: pointer !important;
  color: var(--fit-secondary) !important;
  width: 36px !important;
  height: 36px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 50% !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

#fit-intelligence-widget .fit-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
  color: var(--fit-text) !important;
  transform: rotate(90deg) !important;
}

#fit-intelligence-widget .fit-close-btn svg {
  width: 18px !important;
  height: 18px !important;
  display: block !important;
}

#fit-intelligence-widget .fit-modal-header {
  margin-bottom: 28px !important;
  text-align: left !important;
}

#fit-intelligence-widget .fit-modal-header h2 {
  font-size: 24px !important;
  font-weight: 700 !important;
  color: var(--fit-text) !important;
  line-height: 1.3 !important;
  margin-bottom: 6px !important;
  text-transform: none !important;
}

#fit-intelligence-widget .fit-subtitle {
  color: var(--fit-secondary) !important;
  font-size: 15px !important;
  line-height: 1.4 !important;
}

#fit-intelligence-widget .fit-form-grid {
  display: grid !important;
  grid-template-columns: 1fr 1fr !important;
  gap: 20px !important;
  margin-bottom: 20px !important;
}

#fit-intelligence-widget .fit-form-group {
  display: flex !important;
  flex-direction: column !important;
  gap: 8px !important;
}

#fit-intelligence-widget .fit-form-group label {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: var(--fit-secondary) !important;
  text-align: left !important;
  display: block !important;
  text-transform: none !important;
}

#fit-intelligence-widget .fit-input-wrapper {
  position: relative !important;
  display: flex !important;
  align-items: center !important;
}

#fit-intelligence-widget .fit-input-wrapper input {
  width: 100% !important;
  height: 48px !important;
  min-height: 48px !important;
  padding: 0 40px 0 16px !important;
  border: 1px solid rgba(0, 0, 0, 0.15) !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  font-family: var(--fit-font) !important;
  color: var(--fit-text) !important;
  outline: none !important;
  transition: all 0.2s ease !important;
  background-color: #fff !important;
  box-shadow: none !important;
  text-align: left !important;
}

#fit-intelligence-widget .fit-input-wrapper input:focus {
  border-color: var(--fit-primary) !important;
  box-shadow: 0 0 0 2px var(--fit-primary) !important;
}

#fit-intelligence-widget .fit-unit {
  position: absolute !important;
  right: 16px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  color: var(--fit-secondary) !important;
  pointer-events: none !important;
}

#fit-intelligence-widget .fit-select-wrapper {
  position: relative !important;
}

#fit-intelligence-widget .fit-select-wrapper select {
  width: 100% !important;
  height: 48px !important;
  min-height: 48px !important;
  padding: 0 40px 0 16px !important;
  border: 1px solid rgba(0, 0, 0, 0.15) !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  font-family: var(--fit-font) !important;
  color: var(--fit-text) !important;
  outline: none !important;
  background-color: #fff !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: right 16px center !important;
  background-size: 18px !important;
  appearance: none !important;
  -webkit-appearance: none !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
  text-align: left !important;
}

#fit-intelligence-widget .fit-select-wrapper select:focus {
  border-color: var(--fit-primary) !important;
  box-shadow: 0 0 0 2px var(--fit-primary) !important;
}

#fit-intelligence-widget .fit-submit-btn {
  width: 100% !important;
  background-color: var(--fit-primary) !important;
  color: white !important;
  height: 50px !important;
  min-height: 50px !important;
  border-radius: 8px !important;
  border: none !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  font-family: var(--fit-font) !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  margin-top: 28px !important;
  transition: all 0.2s ease !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

#fit-intelligence-widget .fit-submit-btn:hover {
  filter: brightness(0.95) !important;
}

#fit-intelligence-widget .fit-submit-btn:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
}

#fit-intelligence-widget .fit-arrow-icon {
  width: 18px !important;
  height: 18px !important;
  transition: transform 0.2s ease !important;
  display: inline-block !important;
}

#fit-intelligence-widget .fit-submit-btn:hover .fit-arrow-icon {
  transform: translateX(3px) !important;
}

#fit-intelligence-widget .fit-spinner {
  width: 20px !important;
  height: 20px !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 50% !important;
  border-top-color: white !important;
  animation: fit-spin 0.6s linear infinite !important;
  display: inline-block !important;
}

/* Result Styles */
#fit-intelligence-widget .fit-result-card {
  background-color: rgba(0, 0, 0, 0.02) !important;
  border-radius: 8px !important;
  padding: 24px !important;
  margin-bottom: 24px !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  animation: fit-fade-in 0.3s ease forwards !important;
}

#fit-intelligence-widget .fit-size-badge-outer {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  margin-bottom: 24px !important;
}

#fit-intelligence-widget .fit-size-badge {
  background-color: #fff !important;
  border: 3px solid var(--fit-primary) !important;
  border-radius: 50% !important;
  width: 90px !important;
  height: 90px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05) !important;
  animation: fit-bounce-scale 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards !important;
  padding: 0 !important;
}

#fit-intelligence-widget .fit-size-label {
  font-size: 13px !important;
  font-weight: 700 !important;
  color: var(--fit-secondary) !important;
  letter-spacing: 0.08em !important;
  margin-bottom: 12px !important;
  white-space: nowrap !important;
  word-break: keep-all !important;
  word-wrap: normal !important;
  overflow-wrap: normal !important;
  line-height: 1.2 !important;
  display: block !important;
  text-transform: uppercase !important;
  text-align: center !important;
}

#fit-intelligence-widget .fit-size-val {
  font-size: 40px !important;
  font-weight: 800 !important;
  color: var(--fit-primary) !important;
  line-height: 90px !important;
  display: block !important;
}

#fit-intelligence-widget .fit-confidence-section {
  margin-bottom: 24px !important;
}

#fit-intelligence-widget .fit-confidence-header {
  display: flex !important;
  justify-content: space-between !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: var(--fit-secondary) !important;
  margin-bottom: 6px !important;
}

#fit-intelligence-widget .fit-confidence-val {
  color: var(--fit-primary) !important;
}

#fit-intelligence-widget .fit-progress-bg {
  background-color: rgba(0, 0, 0, 0.08) !important;
  height: 8px !important;
  border-radius: 16px !important;
  overflow: hidden !important;
}

#fit-intelligence-widget .fit-progress-fill {
  background-color: var(--fit-primary) !important;
  display: block !important;
  height: 100% !important;
  min-height: 8px !important;
  border-radius: 16px !important;
  transition: width 1s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

#fit-intelligence-widget .fit-reasons-section h4 {
  font-size: 14px !important;
  font-weight: 700;
  color: var(--fit-text) !important;
  margin-bottom: 10px !important;
  text-align: left !important;
}

#fit-intelligence-widget .fit-reasons-list {
  list-style: none !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 8px !important;
}

#fit-intelligence-widget .fit-reasons-list li {
  display: flex !important;
  align-items: flex-start !important;
  gap: 8px !important;
  font-size: 13px !important;
  color: var(--fit-secondary) !important;
  line-height: 1.4 !important;
  text-align: left !important;
}

#fit-intelligence-widget .fit-check-icon {
  width: 15px !important;
  height: 15px !important;
  color: var(--fit-success) !important;
  flex-shrink: 0 !important;
  margin-top: 2px !important;
  display: inline-block !important;
}

#fit-intelligence-widget .fit-secondary-btn {
  width: 100% !important;
  background-color: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.15) !important;
  color: var(--fit-secondary) !important;
  height: 46px !important;
  min-height: 46px !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  font-family: var(--fit-font) !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

#fit-intelligence-widget .fit-secondary-btn:hover {
  background-color: rgba(0, 0, 0, 0.03) !important;
  color: var(--fit-text) !important;
  border-color: rgba(0, 0, 0, 0.25) !important;
}

#fit-intelligence-widget .fit-secondary-btn svg {
  width: 15px !important;
  height: 15px !important;
  transition: transform 0.4s ease !important;
  display: inline-block !important;
}

#fit-intelligence-widget .fit-secondary-btn:hover svg {
  transform: rotate(-180deg) !important;
}

#fit-intelligence-widget #fit-error {
  background-color: #fef2f2 !important;
  border: 1px solid #fca5a5 !important;
  color: #b91c1c !important;
  padding: 14px 16px !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  margin-top: 16px !important;
  text-align: left !important;
  line-height: 1.4 !important;
  animation: fit-fade-in 0.2s ease !important;
}

/* Animations */
@keyframes fit-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fit-slide-up {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes fit-bounce-scale {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    transform: scale(1.1);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fit-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  #fit-intelligence-widget .fit-modal-content {
    padding: 28px 20px !important;
  }
  #fit-intelligence-widget .fit-form-grid {
    grid-template-columns: 1fr !important;
    gap: 14px !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  #fit-intelligence-widget .fit-modal-overlay,
  #fit-intelligence-widget .fit-modal-content,
  #fit-intelligence-widget .fit-size-badge,
  #fit-intelligence-widget .fit-progress-fill,
  #fit-intelligence-widget .fit-arrow-icon,
  #fit-intelligence-widget .fit-secondary-btn svg {
    transition: none !important;
    animation: none !important;
  }
}
`;

  // Inject standard CSS
  if (typeof document !== "undefined") {
    const styleEl = document.createElement("style");
    styleEl.textContent = stylesCSS;
    document.head.appendChild(styleEl);
  }

  // Export functions
  const exports = {
    AnalyticsBatcher,
    DEFAULT_THEME,
    FitWidget,
    ThemeEngine,
    init: (cfg) => new FitWidget(cfg)
  };

  if (typeof window !== "undefined") {
    window.FitIntelligence = { init: exports.init };
  }

  return exports;
})();
